import useProductActivationApi from '~/services/apis/product-activation'
import usePurchaseApi from '~/services/apis/purchases-info'
import type { VoidInspectionContext } from '~/components/Dialogs/void-inspection-screen.vue'
import type { McaSdkConfig } from '~/types/mca-javascript-sdk'
import { TimeOut } from '~/services/apis/timeout'
import StringMethods from '~/utils/character'
import dayjs from 'dayjs'
import type { PolicyResponse } from '@/types'

const PURCHASE_KEY = 'purchase-request-key'

type PurchaseContext = {
	planInformation: PlanInformationContext | null
	isLoading?: boolean
}
export const providePurchaseContext = (value: MaybeRefOrGetter<PurchaseContext>) => {
	const item = toRef(value)
	provide(PURCHASE_KEY, item)
	return value
}

export const usePurchaseContext = () => {
	return inject<Ref<PurchaseContext | null>>(PURCHASE_KEY, ref(null))
}

export default function usePurchaseInfo(props?: {
	planInformation: MaybeRefOrGetter<PlanInformationContext | null>
	purchaseId?: string
}) {
	const purchaseContext = usePurchaseContext()

	const planInformation = computed(
		() => purchaseContext.value?.planInformation ?? toRef(props?.planInformation).value
	)
	const isLoadingPlan = computed(() => purchaseContext.value?.isLoading ?? false)

	const planId = computed(() => planInformation.value?.id || '')
	const selectedStaff = ref()
	const searchVal = shallowRef('')
	const { getPendingPolicy, getPendingPolicies } = useProductActivationApi()
	const { queryPolicy, comparePolicy } = usePurchaseApi()
	const { policiesTableColumn } = useProducts()
	const { productCategoryType, urlQuery, decryptString, toLower, monthByNumber, planLabel } = useUtils()
	const { toCapitalize } = StringMethods()
	const { initializeSdk } = useMcaSdk()
	const { deviceType } = useMobile()
	const activateModal = shallowRef(false)
	const fetchingPolicy = shallowRef(false)
	const loadingInspection = shallowRef(false)
	const notSupported = shallowRef(false)
	const fetchCount = ref(0)
	const pendingPolicies = ref<PaginationContext<PolicyResponse[]> | null>(null)
	const purchase_id = computed(() => String(planInformation.value?.id ?? ''))

	const inspectionProps = reactive<VoidInspectionContext>({
		title: 'Vehicle Inspection',
		key: 'inspections',
		QrCodeLink: '',
		subTitle: 'Kindly scan with your device to proceed'
	})
	const isInspectionNotSupported = shallowRef(false)
	const planName = computed(() => toCapitalize(planInformation.value?.product?.name || '').replace('care', 'Care'))
	const categoryName = computed(() => toLower(planInformation.value?.product?.productCategory?.name || ''))
	const categoryType = computed(() =>
		productCategoryType(categoryName.value, planInformation.value?.product?.code_name ?? '')
	)
	const isClaimable = computed(() => planInformation.value?.product?.claimable || false)

	const tableRecordColumn = computed(() => {
		const autoColumns = policiesTableColumn.auto
		const comprehensiveColumns = policiesTableColumn.comprehensive
		const gadgetColumns = policiesTableColumn.gadget
		const travelColumns = policiesTableColumn.travel
		const healthColumns = policiesTableColumn.health

		if (categoryType.value?.isAuto) {
			return [...autoColumns]
		} else if (categoryType.value?.isComprehensive) {
			return [...comprehensiveColumns]
		} else if (categoryType.value?.isGadget) {
			return [...gadgetColumns]
		} else if (categoryType.value?.isTravel) {
			return [...travelColumns]
		} else if (categoryType.value.isHealth) {
			return [...healthColumns]
		}
		return []
	})

	const numberOfStaffAdded = computed(() => Number(planInformation.value?.total_staff_added ?? 0))
	const totalStaffSlots = computed(() => Number(planInformation.value?.total_staff ?? 0))
	const numberOfActivatedStaff = computed(() => Number(planInformation.value?.custodian_activated ?? 0))
	const totalStaffPendingSlot = computed(() => planInformation.value?.pending_staff_slot ?? 0)
	const totalDependantSlot = computed(() => planInformation.value?.total_dependant ?? 0)
	const totalPendingDependant = computed(() => planInformation.value?.total_remaining_dependants ?? 0)
	const unAssignedDependantSlot = computed(() => planInformation.value?.total_unused_unassigned_dependant_slot ?? 0)
	const hasDependant = computed(() => Boolean(totalDependantSlot.value ?? false))
	const planType = computed(
		() => String(planInformation.value?.details?.health_plan_type ?? 'standard') as HealthPlanType
	)
	const isFamilyPlan = computed(() => planType.value === 'family')
	const isPlanDeactivated = computed(() => planInformation.value?.is_deactivated || false)
	const provider = computed(() => planInformation.value?.product?.underwriter ?? '')
	const familyRequirement = {
		min: FAMILYPLANMIN,
		max: FAMILYPLANMAX
	}
	const pageHeaderStats = computed(() => {
		const addedKey = !categoryType.value.isHealth ? 'Total Policy Holder Added' : 'Total Staff Added '
		const activatedKey = !categoryType.value.isHealth ? 'Policy Holder Activated' : 'Staff Activated'
		return {
			[addedKey]: {
				value: numberOfStaffAdded.value,
				header_class: 'block sm:min-w-[250px]',
				wrapper_class: 'border-success-500'
			},
			[activatedKey]: {
				value: numberOfActivatedStaff.value || 0,
				paragraph_class: 'text-success-500',
				wrapper_class: 'sm:pl-8'
			}
		}
	})
	const currentPage = computed(() => {
		return useRoute().query?.page as 'submitted' | ''
	})
	const planCaption = computed(() => {
		return categoryType.value?.isAuto ? 'vehicle' : categoryType.value?.isGadget ? 'gadget' : 'staff'
	})
	const coverPeriod = computed(() => {
		if ((categoryType.value.isHealth || categoryType.value?.isTravel) && planInformation.value?.details?.month) {
			return Number(planInformation.value?.details?.month || 1)
		}
		if (planInformation.value?.details?.cover_period) {
			// Return the number value of cover period
			return monthByNumber.value[toLower(planInformation.value.details.cover_period)]
		}
		return 1
	})
	const coverPeriodLabel = computed(() => toLower(planInformation.value?.details?.cover_period ?? ''))
	const premium = computed(() => planInformation.value?.premium ?? 0)
	const pricePerProduct = computed(() => {
		let price = 0
		if (totalDependantSlot.value > 0) {
			price = Number(premium.value) / totalDependantSlot.value
		} else {
			price += Number(premium.value)
		}
		return price
	})
	const staffTypes = shallowRef([
		{
			value: 'Staff',
			key: 'staff'
		},
		{
			value: 'Dependants',
			key: 'dependant'
		}
	])
	const staffType = shallowRef('staff')
	const totalAvailableSlots = computed(() => {
		const staffSlot = planInformation.value?.total_staff || 0
		const dependantSlot = planInformation.value?.total_dependant || 0
		return staffType.value === 'staff' ? staffSlot : dependantSlot
	})
	const totalPendingSlot = computed(() => {
		return planInformation.value?.remaining_staff_slot ?? 0
	})
	const staffAddedCount = computed(() => {
		const staffSlot = planInformation.value?.custodians_added ?? 0
		const dependantSlot = planInformation.value?.total_used_dependant_slot ?? 0
		return staffType.value === 'staff' ? staffSlot : dependantSlot
	})
	const activatedSlot = computed(() => {
		const staff_count = planInformation.value?.staff_policies_count ?? 0
		const dependant_count = planInformation.value?.dependant_policies_count ?? 0
		return staffType.value === 'staff' ? staff_count : dependant_count
	})
	const pendingPolicyCount = computed(() => planInformation.value?.staff_pending_policies_count ?? 0)

	const productCode = computed(() => {
		return planInformation.value?.product.code_name ?? 0
	})
	const isBastion = computed(() => toLower(provider.value) === Providers.bastion)
	const isHygeia = computed(() => toLower(provider.value) === Providers.hygeia)
	const isProductDisabled = computed(() => {
		return categoryType.value.isHealth && isBastion.value && coverPeriodLabel.value === 'annual'
	})
	const productId = computed(() => productInfo.value?.id ?? '')
	const { formatDate } = useCustomDate()
	const expiryDate = computed(() => {
		// Since Health plan is month on month renewal, the renewal date display won't really communicate so what we are showing them is the date they paid for but we use the renewal date when it comes to renewals
		if (categoryType.value.isHealth) {
			return formatDate(
				planInformation.value?.details?.actual_renewal_date ?? planInformation.value?.actual_renewal_date ?? '',
				'DD MMM, YYYY'
			)
		}
		return formatDate(planInformation.value?.renewal_date ?? '', 'DD MMM, YYYY')
	})
	const closeToExpiry = computed(() => {
		const today = dayjs()
		const daysUntilExpiry = dayjs(expiryDate.value).diff(today, 'day')
		return daysUntilExpiry >= 0 && daysUntilExpiry <= 7
	})
	const churnedDate = computed(() => String(planInformation.value?.wait_until ?? ''))
	const churnedDay = computed(() =>
		planInformation.value?.wait_until ? formatDate(new Date(planInformation.value?.wait_until ?? ''), 'DD') : 0
	)
	const planHasSlot = computed(() => {
		return Boolean(planInformation.value?.remaining_staff_slot ?? 0)
	})
	const canRenew = computed(() => planInformation.value?.can_renew || closeToExpiry.value || false)
	const planStatus = computed(() => {
		let status = ''
		if (planInformation.value) {
			if (planInformation.value?.is_expired) {
				status = 'expired'
			} else if (planInformation.value?.is_deactivated) {
				status = 'deactivated'
			} else if (planInformation.value?.is_terminated) {
				status = 'terminated'
			} else {
				status = 'active'
			}
		}
		return status
	})
	const purchaseDate = computed(() => {
		const val = planInformation.value?.purchase_date
			? formatDate(planInformation.value?.purchase_date, 'DD MMM, YYYY')
			: 'n/a'
		return {
			formatted: val,
			raw: planInformation.value?.purchase_date
		}
	})
	const renewalDate = computed(() => {
		const val = planInformation.value?.renewal_date
			? formatDate(planInformation.value?.renewal_date, 'DD MMM, YYYY')
			: 'n/a'
		return {
			formatted: val,
			raw: planInformation.value?.renewal_date
		}
	})
	const canAutoDebit = computed(() => Boolean(planInformation.value?.auto_debit))
	const { getSupportContact } = useStore()
	const supportLine = computed(() => ({
		linkText: getSupportContact('call')?.linkText || '',
		link: getSupportContact('call')?.link || ''
	}))
	const isPlanExpired = computed(() => planInformation.value?.is_expired || false)
	const queuedPendingPoliciesCount = computed(() => planInformation.value?.queued_pending_policies_count)
	const hasAQueuedSlot = computed(() => Boolean(queuedPendingPoliciesCount.value))
	const planLabels = computed(() => planLabel(categoryName.value))
	const productInfo = computed(() => planInformation.value?.product || null)
	const allSlots = computed(() => Number(planInformation.value?.total_quantity ?? 0))

	const next = async (arg: typeof currentPage.value, active_plan_id?: string, refreshFn?: () => void) => {
		if (!active_plan_id) {
			return useNotification().alert({
				status: 400,
				message: 'Something went wrong, Please try again.'
			})
		}
		resolve(() => {
			navigateTo({
				query: {
					page: arg,
					active_id: active_plan_id || ''
				}
			})
			if (active_plan_id && refreshFn) {
				refreshFn()
			}
		})
	}
	const openStaffRecord = async (
		policy: {
			purchase_id: string
			items?: PolicyResponse | null
			customer_id?: string
			preventRequest?: boolean
		},
		fn?: Function
	) => {
		return await resolve(async () => {
			let staffPolicyalreadyExists = false
			const planCategory = String(policy.items?.product?.productCategory?.name).toLowerCase()
			if (planCategory === ProductCategory.Health) {
				const isNewPolicy = await comparePolicy(
					policy?.purchase_id || purchase_id.value,
					policy?.customer_id || (policy.items?.customer_id as string)
				)
				if (isNewPolicy?.data?.existingPolicy) {
					staffPolicyalreadyExists = true
					notSupported.value = true
				}
			}
			if (!staffPolicyalreadyExists) {
				// This is called directly from the custodian page so no need loading the pending policy  again
				if (policy?.preventRequest) {
					activateModal.value = true
					return null
				}
				const res = await getPendingPolicy<PolicyResponse>({
					param: policy?.items?.id
				})
				if (res) {
					notSupported.value = res?.meta?.is_paused || false
					activateModal.value = !res?.meta?.is_paused
					selectedStaff.value = res
					if (fn) {
						fn()
					}
				}

				return null
			}
		}, fetchingPolicy)
	}
	const triggerInspection = async (onCallback: () => void) => {
		const { active_id } = urlQuery.value
		if (active_id) {
			try {
				loadingInspection.value = true
				const result = await queryPolicy(String(active_id))
				if (result?.data) {
					const inspectionLink = result.data.meta.inspectionLink
					if (inspectionLink) {
						const link = new URL(inspectionLink)
						const constructed_url = `${location.origin}${link?.search?.replace('?q=', '?inspectionSdk=')}`
						inspectionProps.QrCodeLink = constructed_url
						if (deviceType.value === 'desktop') {
							isInspectionNotSupported.value = true
							return null
						} else {
							const sdk = decryptString<McaSdkConfig>(inspectionLink)
							initializeSdk({ ...sdk, onClose: () => onCallback(), callback: () => onCallback() })
						}
					} else if (!inspectionLink && fetchCount.value < 20) {
						await TimeOut(5000)
						fetchCount.value++
						loadingInspection.value = true
						await triggerInspection(onCallback)
					} else if (categoryType.value?.isGadget) {
						inspectionProps.QrCodeLink = `${location.origin}/pre-inspection/${active_id}`
						inspectionProps.title = 'Gadget Inspection'
						if (deviceType.value === 'desktop') {
							isInspectionNotSupported.value = true
							return null
						}
						navigateTo(inspectionProps.QrCodeLink)
					} else {
						useNotification().alert({
							message: 'Inspection link not available at the moment, please try again later.',
							status: 400
						})
					}
				}
			} finally {
				loadingInspection.value = false
			}
		} else {
			useNotification().alert({
				status: 400,
				message: 'Plan Id not found'
			})
		}
	}
	const fetchPendingPolicies = async (_: {}, id = purchase_id.value, query?: object) => {
		const result = await getPendingPolicies({ purchase_id: id, ...(query ?? {}) })
		pendingPolicies.value = result
		return result
	}
	const calculatePricePerProduct = (p = premium.value) => Number(p) / allSlots.value

	return {
		isProductDisabled,
		isBastion,
		isHygeia,
		productId,
		provider,
		closeToExpiry,
		planId,
		planLabels,
		queuedPendingPoliciesCount,
		hasAQueuedSlot,
		coverPeriodLabel,
		unAssignedDependantSlot,
		staffType,
		staffTypes,
		totalAvailableSlots,
		totalPendingSlot,
		staffAddedCount,
		activatedSlot,
		pendingPolicyCount,
		productCode,
		churnedDate,
		churnedDay,
		planHasSlot,
		isPlanDeactivated,
		premium,
		purchaseDate,
		renewalDate,
		canAutoDebit,
		supportLine,
		expiryDate,
		canRenew,
		planStatus,
		isPlanExpired,
		familyRequirement,
		isFamilyPlan,
		planType,
		totalStaffPendingSlot,
		totalDependantSlot,
		totalPendingDependant,
		hasDependant,
		totalStaffSlots,
		purchase_id,
		isClaimable,
		isLoadingPlan,
		searchVal,
		deviceType,
		activateModal,
		fetchingPolicy,
		loadingInspection,
		notSupported,
		inspectionProps,
		isInspectionNotSupported,
		pendingPolicies,
		planName,
		categoryName,
		categoryType,
		tableRecordColumn,
		numberOfActivatedStaff,
		currentPage,
		pageHeaderStats,
		planCaption,
		policiesTableColumn,
		next,
		initializeSdk,
		selectedStaff,
		numberOfStaffAdded,
		urlQuery,
		openStaffRecord,
		triggerInspection,
		fetchPendingPolicies,
		planInformation,
		coverPeriod,
		productInfo,
		calculatePricePerProduct
	}
}
